import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { applyLegacyCleanup, previewLegacyCleanup, LegacyCleanupPreview, LegacyCleanupResult } from '@/services/domainKnowledgeService';

export function LegacyKnowledgeCleanup({ siteId, onApplied }: { siteId: string; onApplied: () => void }) {
  const [branch, setBranch] = useState('all');
  const [status, setStatus] = useState('pending_review');
  const [preview, setPreview] = useState<LegacyCleanupPreview | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [reason, setReason] = useState('Retire legacy per-product research questions after review.');
  const [confirmUsed, setConfirmUsed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LegacyCleanupResult | null>(null);
  const selectedUsed = preview?.items.filter(item => selected.includes(item.id) && (item.usageCount ?? 0) > 0).length ?? 0;

  function invalidate() {
    setPreview(null);
    setSelected([]);
    setConfirmUsed(false);
    setResult(null);
    setError(null);
  }

  async function loadPreview() {
    invalidate();
    setBusy(true);
    try {
      const next = await previewLegacyCleanup(siteId, branch, status);
      setPreview(next);
      setSelected(next.items.filter(item => item.defaultSelected).map(item => item.id));
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  async function apply() {
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      const applied = await applyLegacyCleanup(siteId, preview.previewToken, selected, reason, confirmUsed);
      setPreview(null);
      setSelected([]);
      setConfirmUsed(false);
      setResult(applied);
      onApplied();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog>
      <DialogTrigger asChild><Button variant="outline" size="sm">Review legacy questions</Button></DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review legacy research questions</DialogTitle>
          <DialogDescription>
            Review matching records for the selected site before deprecating them. Their content is retained.
            Specification answers are unselected by default.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">Question branch
            <select aria-label="Question branch" className="block border rounded p-2" value={branch} disabled={busy}
              onChange={event => { invalidate(); setBranch(event.target.value); }}>
              <option value="all">All legacy branches</option>
              <option value="hallucination">Fact-check</option>
              <option value="context">Context</option>
              <option value="missing_specification">Specification requirements</option>
            </select>
          </label>
          <label className="text-sm">Status
            <select aria-label="Legacy question status" className="block border rounded p-2" value={status} disabled={busy}
              onChange={event => { invalidate(); setStatus(event.target.value); }}>
              <option value="all">All statuses</option>
              <option value="pending_review">Pending review</option>
              <option value="proposed">Proposed</option>
              <option value="approved">Approved</option>
              <option value="active">Active</option>
              <option value="resolved">Resolved</option>
              <option value="deprecated">Deprecated</option>
            </select>
          </label>
          <Button variant="outline" onClick={loadPreview} disabled={busy}>
            {busy ? 'Working…' : 'Refresh preview'}
          </Button>
        </div>
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        {result && <div role="status" className="rounded border p-3 text-sm">
          Deprecated {result.changedCount} of {result.previewCount} previewed records.
          {' '}{result.untouchedPreviewCount} previewed records were not touched by this action. Content retained.
          <details className="mt-2"><summary>Changed record IDs</summary><ul>
            {result.changedIds.map(id => <li key={id} className="font-mono text-xs">{id}</li>)}
          </ul></details>
        </div>}
        {preview && <>
          <p className="text-sm text-muted-foreground">Matching basis: {preview.matchBasis}</p>
          <p role="status" className="text-sm">
            {preview.items.length} matches; {selected.length} selected; {preview.items.length - selected.length} will be untouched.
          </p>
          <div className="space-y-2">
            {preview.items.map(item => <div key={item.id} className="rounded border p-3 text-sm">
              <label className="flex items-start gap-2">
                <input type="checkbox" checked={selected.includes(item.id)} disabled={busy || !!item.protectedReason}
                  aria-label={`Select ${item.title}`}
                  onChange={event => {
                    setConfirmUsed(false);
                    setSelected(current => event.target.checked ? [...current, item.id] : current.filter(id => id !== item.id));
                  }} />
                <span className="font-medium">{item.title}</span>
              </label>
              <p className="mt-1">{item.researchQuestion}</p>
              <p className="text-muted-foreground">Status: {item.status}; recorded uses: {item.usageCount ?? 'unknown'}.</p>
              <p className="text-muted-foreground">
                Product link: {item.kbeLinked ? 'knowledge entry found' : item.feedLinked ? 'feed item found' : 'unresolved'}.
                {' '}Stored category: {item.scopeCategoryPath || 'unavailable'}.
              </p>
              {item.protectedReason && <p className="text-destructive">{item.protectedReason}</p>}
              <p className="text-xs text-muted-foreground font-mono">{item.id}</p>
            </div>)}
          </div>
          <label className="text-sm">Deprecation reason
            <Input aria-label="Deprecation reason" value={reason} maxLength={1000} disabled={busy}
              onChange={event => setReason(event.target.value)} />
          </label>
          {selectedUsed > 0 && <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={confirmUsed} disabled={busy}
              onChange={event => setConfirmUsed(event.target.checked)} />
            I separately confirm deprecating {selectedUsed} selected record(s) with positive usage. This may change future generation.
          </label>}
          <p className="text-xs text-muted-foreground">Preview expires at {new Date(preview.expiresAt).toLocaleTimeString()}. Refresh after any record changes.</p>
          <Button variant="destructive" onClick={apply}
            disabled={busy || selected.length === 0 || !reason.trim() || (selectedUsed > 0 && !confirmUsed)}>
            Deprecate {selected.length} selected record(s)
          </Button>
        </>}
      </DialogContent>
    </Dialog>
  );
}
